import { FirebaseError } from '@firebase/util'
import { Button, InputAdornment, Stack, Typography } from '@mui/material'
import { forwardRef, useState } from 'react'
import { IconButton, TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { tLabel, useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, SubmitHandler, useForm, UseFormProps } from '@/core/form'
import { validateEmail } from '@/core/validators'

export type EmailAddressFormProps = {
  state: {
    email: string
  }
  onSubmit: SubmitHandler<EmailAddressFormValues>
}

export type EmailAddressFormValues = {
  email: string
  password: string
}

const defaultValues: EmailAddressFormValues = {
  email: '',
  password: '',
}

const schema: UseFormProps['schema'] = o => ({
  email: validateEmail(o, 'emailAddressSettings.email'),
  password: o
    .string()
    .min(8)
    .max(30)
    .label(tLabel('emailAddressSettings.password')),
})

export const EmailAddressSettingForm: Organism<EmailAddressFormProps> = ({
  state,
  onSubmit,
}) => {
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<EmailAddressFormValues>({
    schema,
    mode: 'onChange',
    defaultValues,
  })

  const { t } = useTranslation(['emailAddressSettings', 'firebase'])
  const { showError } = useMessage()

  return (
    <Form
      onSubmit={handleSubmit(
        inputs =>
          onSubmit(inputs)
            .then(() => {
              reset()
            })
            .catch((e: any) => {
              // firebaseの認証エラー
              if (e instanceof FirebaseError && e.code.startsWith('auth/')) {
                const message = t(
                  e.code === 'auth/wrong-password'
                    ? 'emailAddressSettings:wrongPassword'
                    : `firebase:auth.errors.${e.code}`
                )
                showError(new Error(message))
              } else {
                showError(new Error(t('emailAddressSettings:error')))
              }
            }),
        e => {
          // クライアントでのバリデーションエラー

          showError(new Error(t('emailAddressSettings:error')))
          throw e
        }
      )}
    >
      <CurrentEmailTextCell email={state.email} />

      <TextInputCell
        type="email"
        error={errors?.email}
        {...register('email')}
      />
      <PasswordInputCell error={errors?.password} {...register('password')} />
      <DescriptionCell />
      <ButtonCell loading={isSubmitting} />
    </Form>
  )
}

const CurrentEmailTextCell: Cell<{ email: string }> = ({ email }) => {
  const { t } = useTranslation('emailAddressSettings')

  return (
    <CellFrame>
      <Typography
        variant="subtitle1"
        sx={{
          my: 2,
          color: 'grey.600',
        }}
      >
        {t('currentEmailAddress')}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mt: 2,
          mb: 7,
          color: 'text.primary',
        }}
      >
        {email}
      </Typography>
    </CellFrame>
  )
}

const TextInputCell = forwardRef(
  (
    props: Omit<TextInputFieldProps, 't'>,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const { t } = useTranslation('emailAddressSettings')

    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

const PasswordInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't' | 'type'>, ref) => {
    const { t } = useTranslation('emailAddressSettings')
    const [showPassword, setShowPassword] = useState(false)

    return (
      <CellFrame>
        <TextInputField
          type={showPassword ? 'text' : 'password'}
          {...props}
          textFieldProps={{
            ...props.textFieldProps,
            InputProps: {
              ...props.textFieldProps?.InputProps,
              inputProps: { variant: 'body2', sx: { px: 4, py: 3 } },
              endAdornment: (
                <InputAdornment position="end">
                  {showPassword ? (
                    <IconButton.Display
                      onClick={() => setShowPassword(prevState => !prevState)}
                    />
                  ) : (
                    <IconButton.Hide
                      onClick={() => setShowPassword(prevState => !prevState)}
                    />
                  )}
                </InputAdornment>
              ),
            },
          }}
          ref={ref}
          t={t}
        />
      </CellFrame>
    )
  }
)

const DescriptionCell: Cell = () => {
  const { t } = useTranslation('emailAddressSettings')
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Typography variant="body2">
        {t('emailAddressSettings:emailAddressInputDescription')}
      </Typography>
    </CellFrame>
  )
}

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('emailAddressSettings')
  const { router } = useRouter()

  return (
    <CellFrame sx={{ px: 4, py: 3, textAlign: 'center' }}>
      <Stack spacing={1} alignItems="center">
        <Button
          type="submit"
          size="large"
          sx={{ width: 244 }}
          loading={loading}
        >
          {t('submit')}
        </Button>
        <Button
          variant="text"
          size="large"
          sx={{ width: 244 }}
          onClick={() => router.goBack()}
        >
          {t('back')}
        </Button>
      </Stack>
    </CellFrame>
  )
}
